// LocalCandidates.js
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import { Search, BoxArrowUpRight } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useLocalCandidates from '../../hooks/useLocalCandidates';
import { encodeLocalCandidateRef } from '../../utils/localCandidateRef';

import '../feed/FeedItem.css';
import './StateCandidates.css';
import './LocalCandidates.css';

/**
 * LocalCandidates
 *
 * State, county and city candidates from api2, as a sibling to StateCandidates
 * rather than a mode of it.
 *
 * Rows open a detail page, the way federal rows do, but they cannot use the
 * same route: /us/candidate/:candidateId is built from an FEC candidate ID, and
 * nothing below federal level has one. These go to
 * /us/candidate/local/:stateAbbr/:level/:ref instead, where the ref is composed
 * from county, office and name -- see utils/localCandidateRef.
 *
 * That composition is done here rather than on the card's click handler so the
 * ref is built once from the same row the card renders, and a card that cannot
 * produce one is not clickable rather than navigating to a page that will not
 * resolve.
 *
 * @param {Object}   props
 * @param {string}   props.stateAbbr
 * @param {string}   props.level      - 'state' | 'county' | 'city' | 'local'
 * @param {string}   [props.county]
 * @param {Function} [props.onFacets] - Reports the counties found, so the page
 *                                      can build a filter from real data.
 * @param {boolean}  [props.countyFilterAvailable] - Whether the page is showing
 *                                      a county picker. Decides whether the
 *                                      truncation notice can tell the reader to
 *                                      narrow by county, or would be naming a
 *                                      control that is not on screen.
 * @param {string}   [props.externalQuery] - Search text owned by the host page.
 *                                      Supplied where the page has taken over
 *                                      the app's shared search bar, so this
 *                                      does not render a second search box
 *                                      beside a live one.
 */

const PAGE_SIZE = 10;

function LocalCandidates({
  stateAbbr,
  level,
  county = '',
  year,
  showSearch = true,
  initialCount = PAGE_SIZE,
  onFacets,
  countyFilterAvailable = false,
  externalQuery = null,
}) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  // Defaults in the destructure rather than trust. A hook that came back with
  // an unexpected shape used to take the whole page down with a TypeError three
  // lines later, which says nothing about what actually went wrong -- an empty
  // list and a logged warning are recoverable and name the problem.
  const hookResult = useLocalCandidates({
    stateAbbr,
    level,
    county,
    year,
  });

  if (!hookResult || !Array.isArray(hookResult.candidates)) {
    console.error('[LocalCandidates] useLocalCandidates returned an unexpected shape', {
      stateAbbr,
      level,
      county,
      hookResult,
    });
  }

  const {
    candidates = [],
    counties = [],
    loading = false,
    error = null,
    truncated = false,
    authority = null,
  } = hookResult || {};

  const [ownQuery, setOwnQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(initialCount);

  // The host page wins where it has one. RepresentativesPage registers the
  // app's shared search bar while it is mounted, and a second box under it
  // would be two controls doing the same job with different contents.
  const usingExternalQuery = externalQuery !== null;
  const query = usingExternalQuery ? externalQuery : ownQuery;

  // Hand the discovered counties up so the page's filter lists only counties
  // that actually have a published field, rather than every county in the state.
  useEffect(() => {
    if (onFacets) onFacets({ counties, truncated });
  }, [counties, truncated, onFacets]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return candidates;

    return candidates.filter((candidate) => {
      const haystack = [
        candidate.displayName,
        candidate.office,
        candidate.party,
        candidate.county,
        candidate.jurisdiction,
        candidate.district ? `district ${candidate.district}` : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(needle);
    });
  }, [candidates, query]);

  const visible = filtered.slice(0, visibleCount);

  // A new search starts at the top of its own results rather than however far
  // the reader had paged through the previous ones.
  useEffect(() => {
    setVisibleCount(initialCount);
  }, [query, initialCount]);

  const openCandidate = (candidate) => {
    // Null when the row carried no id, which leaves the card inert rather than
    // navigating to a page that cannot resolve.
    const ref = encodeLocalCandidateRef(candidate);
    if (!ref) return;
    window.scrollTo(0, 0);
    navigate(`/us/candidate/local/${stateAbbr}/${level}/${ref}`);
  };

  if (loading) {
    return (
      <div className="state-candidates-status">
        <Spinner id="spinner" animation="grow" variant="light" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="member-detail-text">
        {t('common:errorPrefix', 'Error: {{message}}', { message: error.message })}
      </p>
    );
  }

  if (!candidates.length) {
    // Twenty-two states publish no bulk list below federal level. Saying "no
    // candidates on file" in one of them would be false -- there is a full
    // ballot, the state just does not publish it in a form anyone can read in
    // bulk. So when the state's own election authority is known, the empty
    // state points at it rather than closing the question.
    return (
      <div className="local-candidates-empty">
        <p className="member-detail-text">
          {authority
            ? t(
                'common:stateNoBulkCandidates',
                'This state does not publish a full candidate list in bulk, so we cannot show one here.'
              )
            : t('common:noLocalCandidates', 'No candidates are on file for this yet.')}
        </p>

        {authority && (
          <a
            className="local-candidate-action"
            href={authority.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {authority.agencyName
              ? t('common:checkAuthority', 'Check {{agency}}', {
                  agency: authority.agencyName,
                })
              : t('common:checkStateElectionOffice', 'Check the state election office')}
            <BoxArrowUpRight className="ms-2" size={12} />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="state-candidates local-candidates">
      {showSearch && !usingExternalQuery && (
        <Form
          className="state-candidates-search"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Form.Group className="d-flex w-100">
            <Form.Control
              id="searchInput"
              type="text"
              placeholder={t('common:searchCandidates', 'Search candidates')}
              value={query}
              onChange={(e) => {
                setOwnQuery(e.target.value);
                setVisibleCount(initialCount);
              }}
              className="flex-grow-1"
            />
            <Button id="ggButton" type="submit" aria-label={t('common:search', 'Search')}>
              <Search />
            </Button>
          </Form.Group>
        </Form>
      )}

      {/* Said out loud rather than left for the reader to notice. Indiana files
          8,670 non-federal candidates, so a state-wide list is genuinely cut,
          and a silent cut reads as a state with fewer races than it has.
          
          The advice is only given where it can be taken. Telling somebody to
          narrow by county on a screen with no county picker -- a state-level
          list, or a state whose rows carry no county at all -- names a control
          that is not there, and the search box below filters only the rows
          already loaded, so it cannot reach the rest either. */}
      {truncated && !query && (
        <div className="local-candidates-truncated">
          {countyFilterAvailable && !county
            ? t(
                'common:candidatesTruncatedNarrow',
                'Showing the first {{count}}. Choose a county to see the rest.',
                { count: candidates.length }
              )
            : t('common:candidatesTruncatedPlain', 'Showing the first {{count}}.', {
                count: candidates.length,
              })}
        </div>
      )}

      {visible.length === 0 ? (
        <p className="member-detail-text">
          {t('common:noResultsFound', 'No results found.')}
        </p>
      ) : (
        visible.map((candidate) => {
          // Assembled by filtering so a missing party or county never leaves a
          // dangling separator.
          const meta = [
            candidate.party,
            candidate.county || candidate.jurisdiction,
            candidate.district
              ? `${t('common:district', 'District')} ${candidate.district}`
              : null,
          ].filter(Boolean);

          return (
            <div className="feed-item feed-item--default mb-4" key={candidate.key}>
              <Card>
                <Card.Title
                  id="billText"
                  className="card-title p-3"
                  onClick={() => openCandidate(candidate)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openCandidate(candidate);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                >
                  {candidate.displayName}
                </Card.Title>

                <Card.Body id="billBodyText">
                  {candidate.office} {t('common:candidateSuffix', '(Candidate)')}
                </Card.Body>

                {meta.length > 0 && (
                  <Card.Body id="billBodyText">{meta.join(' · ')}</Card.Body>
                )}

                {candidate.year && (
                  <Card.Body id="billBodyText">
                    {t('common:electionYear', 'Election: {{year}}', {
                      year: candidate.year,
                    })}
                  </Card.Body>
                )}

     
              </Card>
            </div>
          );
        })
      )}

      {visibleCount < filtered.length && (
        <div className="state-candidates-more">
          <Button
            id="standardButton"
            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
          >
            {t('common:more', 'More')}
          </Button>
        </div>
      )}
    </div>
  );
}

export default LocalCandidates;